import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import FollowupPopup from './FollowupPopup';
import { 
  LayoutDashboard, 
  MessageSquare, 
  FolderKanban, 
  Users, 
  FileText, 
  Settings, 
  LogOut, 
  User,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Lead CRM', path: '/admin/leads', icon: FolderKanban },
    { name: '📲 Mobile App', path: '/admin/mobile-app', icon: Smartphone },
  ];

  if (user?.role === 'super_admin') {
    navItems.push(
      { name: 'Users', path: '/admin/users', icon: Users },
      { name: 'Blogs', path: '/admin/blogs', icon: FileText },
      { name: 'Services', path: '/admin/services', icon: Settings },
      { name: 'Documents', path: '/admin/documents', icon: FolderOpen }
    );
  }

  const getProfilePhotoUrl = () => {
    if (user?.profile_photo) {
      // Use API endpoint for profile photos
      const photoPath = user.profile_photo.replace('/uploads/', '/api/uploads/');
      return `${API_URL}${photoPath}`;
    }
    return null;
  };

  const isActive = (path) => {
    if (path === '/admin/leads') {
      return location.pathname === path || location.pathname.startsWith('/admin/lead');
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside 
        className={`bg-white shadow-xl fixed left-0 top-0 h-full z-50 transition-all duration-300 flex flex-col ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo Section */}
        <div className="p-4 border-b flex items-center justify-between">
          {!sidebarCollapsed && (
            <Link to="/admin/dashboard">
              <img src="/company-logo.png" alt="Logo" className="h-10" />
            </Link>
          )}
          {sidebarCollapsed && (
            <Link to="/admin/dashboard" className="mx-auto">
              <img src="/company-logo.png" alt="Logo" className="h-8 w-8 object-contain" />
            </Link>
          )}
          
          {/* Mobile close button */}
          <button 
            className="md:hidden p-1 rounded hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                      active
                        ? 'bg-[#0A2463] text-white shadow-lg'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-[#0A2463]'
                    }`}
                    title={sidebarCollapsed ? item.name : ''}
                  >
                    <Icon size={20} className={sidebarCollapsed ? 'mx-auto' : ''} />
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Profile Section */}
        <div className="border-t p-4">
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            {getProfilePhotoUrl() ? (
              <img 
                src={getProfilePhotoUrl()} 
                alt={user?.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                user?.role === 'super_admin' 
                  ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                  : 'bg-gradient-to-br from-blue-500 to-cyan-500'
              }`}>
                <User className="text-white" size={18} />
              </div>
            )}
            
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{user?.name}</div>
                <div className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</div>
              </div>
            )}
          </div>
          
          {!sidebarCollapsed && (
            <Button
              onClick={logout}
              size="sm"
              variant="outline"
              className="w-full mt-3 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          )}
          
          {sidebarCollapsed && (
            <Button
              onClick={logout}
              size="sm"
              variant="outline"
              className="w-full mt-3 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              title="Logout"
            >
              <LogOut size={16} />
            </Button>
          )}
        </div>

        {/* Collapse Toggle Button - Desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden md:flex absolute -right-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md rounded-full p-1.5 hover:bg-gray-50 transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm sticky top-0 z-30 px-4 md:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>

            {/* Page Title - Can be dynamic based on route */}
            <div className="hidden md:block">
              <h1 className="text-xl font-semibold text-[#0A2463]">
                {navItems.find(item => isActive(item.path))?.name || 'Admin Panel'}
              </h1>
            </div>

            {/* Right Section - Contact & Quick Actions */}
            <div className="flex items-center gap-4">
              <a 
                href="tel:9316085383" 
                className="hidden lg:flex items-center gap-1 text-sm text-gray-600 hover:text-[#0A2463]"
              >
                📞 9316085383
              </a>
              <a 
                href="https://wa.me/919755085383" 
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
              >
                💬 WhatsApp
              </a>
              
              {/* Mobile User Avatar */}
              <div className="md:hidden flex items-center gap-2">
                {getProfilePhotoUrl() ? (
                  <img 
                    src={getProfilePhotoUrl()} 
                    alt={user?.name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    user?.role === 'super_admin' 
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                      : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  }`}>
                    <User className="text-white" size={14} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      <FollowupPopup />
    </div>
  );
};
